import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  eventMeshGovernanceApi,
  type ConsumerRegistryEntry,
  type DeliveryPolicy,
  type EventLineageEntry,
  type EventMeshDashboard,
  type EventMeshHealth,
  type EventRegistryEntry,
  type ProducerRegistryEntry,
  type ReplaySummary,
  type RoutingRule,
  type SchemaRegistryEntry,
  type SchemaValidationResult,
} from '../../api/eventplatform';

type Tab = 'overview' | 'catalog' | 'schemas' | 'routing' | 'replay' | 'deadletter' | 'producers' | 'consumers' | 'health';
type Theme = 'light' | 'dark';

function kindColor(kind: string, dark: boolean) {
  if (kind === 'domain' || kind === 'business') return dark ? 'text-indigo-400' : 'text-indigo-600';
  if (kind === 'integration') return dark ? 'text-cyan-400' : 'text-cyan-600';
  if (kind === 'automation' || kind === 'workflow') return dark ? 'text-violet-400' : 'text-violet-600';
  return dark ? 'text-slate-400' : 'text-slate-600';
}

export function EventMeshGovernancePage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<EventMeshDashboard | null>(null);
  const [events, setEvents] = useState<EventRegistryEntry[]>([]);
  const [schemas, setSchemas] = useState<SchemaRegistryEntry[]>([]);
  const [producers, setProducers] = useState<ProducerRegistryEntry[]>([]);
  const [consumers, setConsumers] = useState<ConsumerRegistryEntry[]>([]);
  const [routing, setRouting] = useState<RoutingRule[]>([]);
  const [policies, setPolicies] = useState<DeliveryPolicy[]>([]);
  const [health, setHealth] = useState<EventMeshHealth | null>(null);
  const [lineage, setLineage] = useState<EventLineageEntry[]>([]);
  const [replay, setReplay] = useState<ReplaySummary | null>(null);
  const [domainFilter, setDomainFilter] = useState('');
  const [validateResult, setValidateResult] = useState<SchemaValidationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, evt, sch, prod, cons, rt, hlth, lin, rpl] = await Promise.all([
        eventMeshGovernanceApi.dashboard(),
        eventMeshGovernanceApi.listMeshEvents(),
        eventMeshGovernanceApi.listSchemas(),
        eventMeshGovernanceApi.listProducers(),
        eventMeshGovernanceApi.listMeshConsumers(),
        eventMeshGovernanceApi.routing(),
        eventMeshGovernanceApi.meshHealth(),
        eventMeshGovernanceApi.lineage(),
        eventMeshGovernanceApi.replaySummary(),
      ]);
      setDashboard(dash);
      setEvents(evt);
      setSchemas(sch);
      setProducers(prod);
      setConsumers(cons);
      setRouting(rt);
      setPolicies(dash.delivery_policies ?? []);
      setHealth(hlth);
      setLineage(lin);
      setReplay(rpl);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load event mesh');
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const validateSchema = async (eventName: string) => {
    setBusy(true);
    setValidateResult(null);
    try {
      const result = await eventMeshGovernanceApi.validateSchema({ event_name: eventName });
      setValidateResult(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Validation failed');
    } finally {
      setBusy(false);
    }
  };

  const domains = [...new Set(events.map((e) => e.domain))];
  const filteredEvents = domainFilter ? events.filter((e) => e.domain === domainFilter) : events;

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'catalog', label: 'Event Catalog' },
    { id: 'schemas', label: 'Schema Explorer' },
    { id: 'routing', label: 'Routing Console' },
    { id: 'replay', label: 'Replay Console' },
    { id: 'producers', label: 'Producer Registry' },
    { id: 'consumers', label: 'Consumer Registry' },
    { id: 'health', label: 'Health Dashboard' },
  ];

  const card = dark ? 'border-slate-800 bg-slate-900' : 'border-slate-200 bg-white';
  const muted = dark ? 'text-slate-400' : 'text-slate-500';
  const shell = dark ? 'bg-slate-950 text-slate-100' : 'bg-white text-slate-900';

  return (
    <div className={`space-y-6 rounded-xl p-1 ${shell}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-violet-500">DEV-000079</p>
          <h1 className="text-2xl font-semibold">Enterprise Event Mesh</h1>
          <p className={`text-sm ${muted}`}>
            Event registry, schema governance, routing, replay & cross-domain event explorer
          </p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <Link to="/events" className="text-violet-500 hover:underline">Event Hub</Link>
            <span className={muted}>·</span>
            <Link to="/platform/integration-governance" className="text-violet-500 hover:underline">Integration Governance</Link>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg border px-3 py-2 text-sm ${card}`} aria-label="Toggle dark mode">
            {dark ? 'Light' : 'Dark'}
          </button>
          <button type="button" onClick={refresh} disabled={busy} className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50">
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className={`rounded-lg border px-4 py-3 text-sm ${dark ? 'border-red-900 bg-red-950 text-red-300' : 'border-red-200 bg-red-50 text-red-700'}`}>{error}</div>
      )}
      {validateResult && (
        <div className={`rounded-lg border px-4 py-3 text-sm ${validateResult.valid ? (dark ? 'border-emerald-900 bg-emerald-950 text-emerald-300' : 'border-emerald-200 bg-emerald-50 text-emerald-800') : (dark ? 'border-amber-900 bg-amber-950 text-amber-300' : 'border-amber-200 bg-amber-50 text-amber-800')}`}>
          {validateResult.message}
        </div>
      )}

      <div className="flex flex-wrap gap-2 border-b pb-2" role="tablist">
        {tabs.map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${tab === t.id ? 'bg-violet-600 text-white' : dark ? 'text-slate-400 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-100'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && health && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Event Health Score</p><p className="mt-1 text-3xl font-semibold text-violet-500">{health.event_health_score.toFixed(1)}</p></div>
          <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Registered Events</p><p className="mt-1 text-3xl font-semibold">{health.registered_events}</p></div>
          <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Throughput / min</p><p className="mt-1 text-3xl font-semibold">{health.event_throughput_per_min.toLocaleString()}</p></div>
          <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Delivery Success</p><p className="mt-1 text-3xl font-semibold">{health.delivery_success_rate.toFixed(1)}%</p></div>
          <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Consumer Lag</p><p className="mt-1 text-2xl font-semibold">{health.consumer_lag_ms.toFixed(0)} ms</p></div>
          <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Schema Compliance</p><p className="mt-1 text-2xl font-semibold">{health.schema_compliance.toFixed(1)}%</p></div>
          <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Dead Letter Rate</p><p className="mt-1 text-2xl font-semibold">{health.dead_letter_rate.toFixed(2)}%</p></div>
          <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Active Producers</p><p className="mt-1 text-2xl font-semibold">{health.active_producers}</p></div>
        </div>
      )}

      {tab === 'catalog' && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor="domain-filter" className={`text-sm ${muted}`}>Domain</label>
            <select id="domain-filter" value={domainFilter} onChange={(e) => setDomainFilter(e.target.value)} className={`rounded-lg border px-3 py-1.5 text-sm ${card}`}>
              <option value="">All domains</option>
              {domains.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div className="overflow-x-auto rounded-xl border">
            <table className={`min-w-full text-left text-sm ${card}`}>
              <thead className={dark ? 'bg-slate-800' : 'bg-slate-50'}>
                <tr>
                  <th className="px-4 py-2 font-medium">Event</th>
                  <th className="px-4 py-2 font-medium">Domain</th>
                  <th className="px-4 py-2 font-medium">Kind</th>
                  <th className="px-4 py-2 font-medium">Producer</th>
                  <th className="px-4 py-2 font-medium">Consumers</th>
                </tr>
              </thead>
              <tbody>
                {filteredEvents.map((e) => (
                  <tr key={e.id} className={`border-t ${dark ? 'border-slate-800' : 'border-slate-100'}`}>
                    <td className="px-4 py-2"><div className="font-medium">{e.event_name}</div><div className={`text-xs ${muted}`}>v{e.version}</div></td>
                    <td className="px-4 py-2">{e.domain}</td>
                    <td className={`px-4 py-2 font-medium ${kindColor(e.kind, dark)}`}>{e.kind}</td>
                    <td className="px-4 py-2">{e.producer}</td>
                    <td className={`px-4 py-2 text-xs ${muted}`}>{e.consumers?.join(', ') || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'schemas' && (
        <div className="overflow-x-auto rounded-xl border">
          <table className={`min-w-full text-left text-sm ${card}`}>
            <thead className={dark ? 'bg-slate-800' : 'bg-slate-50'}>
              <tr>
                <th className="px-4 py-2 font-medium">Event</th>
                <th className="px-4 py-2 font-medium">Format</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {schemas.map((s) => (
                <tr key={s.id} className={`border-t ${dark ? 'border-slate-800' : 'border-slate-100'}`}>
                  <td className="px-4 py-2"><div className="font-medium">{s.event_name}</div><div className={`text-xs ${muted}`}>v{s.version}</div></td>
                  <td className="px-4 py-2">{s.format}</td>
                  <td className="px-4 py-2">{s.compatibility_status}</td>
                  <td className="px-4 py-2">
                    <button type="button" disabled={busy} onClick={() => validateSchema(s.event_name)} className="text-xs text-violet-500 hover:underline disabled:opacity-50">Validate</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'routing' && (
        <div className="grid gap-4 md:grid-cols-2">
          {routing.map((r) => (
            <div key={r.id} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex items-start justify-between">
                <p className="font-semibold">{r.name}</p>
                <span className={`text-xs ${r.enabled ? 'text-emerald-500' : muted}`}>{r.enabled ? 'enabled' : 'disabled'}</span>
              </div>
              <p className={`mt-1 text-xs ${muted}`}>{r.strategy} · {r.event_pattern} → {r.target}</p>
              <p className={`mt-1 text-xs ${muted}`}>Priority {r.priority}{r.domain ? ` · ${r.domain}` : ''}</p>
            </div>
          ))}
          {policies.map((p) => (
            <div key={p.id} className={`rounded-xl border p-4 ${card}`}>
              <p className="font-semibold">{p.name}</p>
              <p className={`text-xs uppercase ${muted}`}>{p.mode}</p>
              <p className={`mt-1 text-xs ${muted}`}>Retry {p.retry_max} · Backoff {p.backoff_ms}ms · DLQ {p.dlq_enabled ? 'on' : 'off'}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'replay' && replay && (
        <div className={`rounded-xl border p-6 ${card}`}>
          <div className="grid gap-4 sm:grid-cols-4">
            <div><p className={`text-xs uppercase ${muted}`}>Total Jobs</p><p className="text-2xl font-semibold">{replay.total_jobs}</p></div>
            <div><p className={`text-xs uppercase ${muted}`}>Completed</p><p className="text-2xl font-semibold">{replay.completed}</p></div>
            <div><p className={`text-xs uppercase ${muted}`}>Pending</p><p className="text-2xl font-semibold">{replay.pending}</p></div>
            <div><p className={`text-xs uppercase ${muted}`}>Events Replayed</p><p className="text-2xl font-semibold">{replay.events_replayed}</p></div>
          </div>
          <Link to="/events/replay" className="mt-4 inline-block text-sm text-violet-500 hover:underline">Open Replay Manager →</Link>
        </div>
      )}

      {tab === 'producers' && (
        <div className="overflow-x-auto rounded-xl border">
          <table className={`min-w-full text-left text-sm ${card}`}>
            <thead className={dark ? 'bg-slate-800' : 'bg-slate-50'}>
              <tr><th className="px-4 py-2 font-medium">Service</th><th className="px-4 py-2 font-medium">Event</th><th className="px-4 py-2 font-medium">Version</th><th className="px-4 py-2 font-medium">Status</th></tr>
            </thead>
            <tbody>
              {producers.map((p) => (
                <tr key={p.id} className={`border-t ${dark ? 'border-slate-800' : 'border-slate-100'}`}>
                  <td className="px-4 py-2">{p.service}</td>
                  <td className="px-4 py-2 font-mono text-xs">{p.event_name}</td>
                  <td className="px-4 py-2">{p.version}</td>
                  <td className="px-4 py-2">{p.active ? 'active' : 'inactive'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'consumers' && (
        <div className="overflow-x-auto rounded-xl border">
          <table className={`min-w-full text-left text-sm ${card}`}>
            <thead className={dark ? 'bg-slate-800' : 'bg-slate-50'}>
              <tr><th className="px-4 py-2 font-medium">Service</th><th className="px-4 py-2 font-medium">Event</th><th className="px-4 py-2 font-medium">Lag</th><th className="px-4 py-2 font-medium">Health</th></tr>
            </thead>
            <tbody>
              {consumers.map((c) => (
                <tr key={c.id} className={`border-t ${dark ? 'border-slate-800' : 'border-slate-100'}`}>
                  <td className="px-4 py-2">{c.service}</td>
                  <td className="px-4 py-2 font-mono text-xs">{c.event_name}</td>
                  <td className="px-4 py-2">{c.consumer_lag_ms} ms</td>
                  <td className="px-4 py-2">{c.health}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'health' && health && (
        <div className={`rounded-xl border p-6 ${card}`}>
          <div className="flex items-center gap-4">
            <div className="text-5xl font-bold text-violet-500">{health.event_health_score.toFixed(1)}</div>
            <div><p className="font-semibold">Event Mesh Health Score</p><p className={`text-sm ${muted}`}>Validated {new Date(health.validated_at).toLocaleString()}</p></div>
          </div>
          <dl className="mt-6 grid gap-3 sm:grid-cols-2">
            <div><dt className={`text-xs uppercase ${muted}`}>Retry Rate</dt><dd>{health.retry_rate.toFixed(2)}%</dd></div>
            <div><dt className={`text-xs uppercase ${muted}`}>Active Consumers</dt><dd>{health.active_consumers}</dd></div>
            <div><dt className={`text-xs uppercase ${muted}`}>Replay Count</dt><dd>{health.replay_count}</dd></div>
            <div><dt className={`text-xs uppercase ${muted}`}>Lineage Entries</dt><dd>{lineage.length}</dd></div>
          </dl>
        </div>
      )}

      {dashboard && tab === 'overview' && (
        <p className={`text-xs ${muted}`}>Tenant {dashboard.tenant_id} · {events.length} events · {schemas.length} schemas</p>
      )}
    </div>
  );
}
